/**
 * Account/Authentication Context
 * 
 * This file implements a centralized authentication state management system for a React application
 * using modern React patterns and browser storage persistence.
 * 
 * Technologies Used:
 * - React 18+ - Using modern hooks (useContext, useReducer) and TypeScript support
 * - TypeScript - Full type safety with interfaces, type unions, and generic types
 * - Context API - React's built-in state management solution for global state
 * - useReducer Hook - For complex state management with predictable state transitions
 * - localStorage API - Browser storage for state persistence across sessions
 * 
 * General Purpose:
 * This code provides a centralized authentication state management system that:
 * 1. Manages JWT Token State - Stores and manages JWT authentication tokens
 * 2. Tracks Authentication Status - Maintains boolean flag for user authentication state
 * 3. Provides State Persistence - Automatically saves/loads state to/from localStorage
 * 4. Offers Predictable State Updates - Uses reducer pattern with defined actions (LOGIN, LOGOUT, UPDATE_TOKEN, CLEAR_AUTH)
 * 5. Enables Global Access - Makes authentication state available throughout the React component tree
 * 6. Includes Error Handling - Gracefully handles localStorage failures and invalid stored data
 * 
 * The context follows React best practices with proper TypeScript typing, error boundaries 
 * for the custom hook, and a clean separation of concerns between state management, 
 * persistence, and component integration.
 * 
 * (Last updated: 2025-10-15)
 */

import React, { createContext, useContext, useReducer } from 'react';
import type { ReactNode } from 'react';

// Define the state interface
export interface AccountState {
  jwtToken: string | null;
  isAuthenticated: boolean;
  userId: string | null;
}

// LocalStorage key constant
const STORAGE_KEY = 'account-state-v1';
const STORAGE_KEY_JWT = 'account-state-v1/jwtToken';

// Functions to handle localStorage
function loadStateFromStorage(): AccountState | null {
  let jwtToken = null; // Default to no token, we use the cookie for regular api calls now.
  let isAuthenticated = false; // Default to not authenticated
  let userId = null;

  if (typeof document !== 'undefined') {
    const match = document.cookie.match(/(?:^|;\s*)loggedin=([^;]+)/);
    isAuthenticated = !!match && match[1].trim() !== '';
    userId = isAuthenticated ? match[1].trim() : null;
  }

  return { jwtToken, isAuthenticated, userId };

  //   let jwtToken = localStorage.getItem(STORAGE_KEY_JWT) ?? null; // JWT is from a separate key
  // try {
  //   const stored = localStorage.getItem(STORAGE_KEY);
  //   if (stored) {
  //     const parsed = JSON.parse(stored);
  //     // Validate that the parsed object has the expected structure
  //     if (
  //       typeof parsed === 'object' &&
  //       parsed !== null &&
  //       typeof parsed.isAuthenticated === 'boolean' 
  //     ) {
  //       return {...parsed, jwtToken} as AccountState;
  //     }
  //   }
  // } catch (error) {
  //   console.warn('Failed to load account state from localStorage:', error);
  // }
  // return null;
}

function saveStateToStorage(state: AccountState): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    /* We store the JWT token separately for easier access outside of the react stuff */
    localStorage.setItem(STORAGE_KEY_JWT, state.jwtToken ? state.jwtToken : '');
  } catch (error) {
    console.warn('Failed to save account state to localStorage:', error);
  }
}

export function getStoredJWTToken(): string | null {
  try {
    const token = localStorage.getItem(STORAGE_KEY_JWT);
    return token ? token : null;
  } catch (error) {
    console.warn('Failed to get JWT token from localStorage:', error);
    return null;
  }
}

// Define action types
export type AccountAction =
  | { type: 'LOGIN'; payload: { jwtToken?: string; userId?: string } }
  | { type: 'LOGOUT' }
  | { type: 'UPDATE_TOKEN'; payload: { jwtToken: string } }
  | { type: 'CLEAR_AUTH' };

// Initial state
const defaultState: AccountState = {
  jwtToken: null,
  isAuthenticated: false,
  userId: null,
};

// Get initial state from localStorage or use default
const getInitialState = (): AccountState => {
  const storedState = loadStateFromStorage();
  return storedState || defaultState;
};

// Reducer function
function accountReducer(
  state: AccountState,
  action: AccountAction
): AccountState {
  let newState: AccountState;

  switch (action.type) {
    case 'LOGIN':
      newState = {
        ...state,
        jwtToken: action.payload.jwtToken ?? state.jwtToken,
        userId: action.payload.userId ?? state.userId,
        isAuthenticated: true,
      };
      break;

    case 'LOGOUT':
      newState = {
        ...state,
        jwtToken: null,
        isAuthenticated: false,
      };
      break;

    case 'UPDATE_TOKEN':
      newState = {
        ...state,
        jwtToken: action.payload.jwtToken,
        isAuthenticated: true,
      };
      break;

    case 'CLEAR_AUTH':
      newState = {
        ...defaultState,
      };
      break;

    default:
      return state;
  }

  // Save to localStorage after state changes
  saveStateToStorage(newState);
  return newState;
}

// Define context type
interface AccountContextType {
  state: AccountState;
  dispatch: React.Dispatch<AccountAction>;
}

// Create the context
const AccountContext = createContext<AccountContextType | undefined>(undefined);

// Provider props
interface AccountProviderProps {
  children: ReactNode;
}

// Provider component
export function AccountProvider({ children }: AccountProviderProps) {
  const [state, dispatch] = useReducer(accountReducer, undefined, getInitialState);

  return (
    <AccountContext.Provider value={{ state, dispatch }}>
      {children}
    </AccountContext.Provider>
  );
}

// Custom hook to use the context
export function useAccount() {
  const context = useContext(AccountContext);
  if (context === undefined) {
    throw new Error('useAccount must be used within an AccountProvider');
  }
  return context;
}
